import { useState, useEffect, useCallback } from 'react';
import { useLocalStorage } from './useLocalStorage';

/**
 * Recorrido de primer ingreso («Cómo funciona AlertAI», maqueta m4-bienvenida del 19-10, rehecho el 01-11-2026).
 *
 * - Se abre solo la primera vez (medio segundo después de montar, para que la página se dibuje antes).
 * - «Empezar» lo marca como visto; «No volver a mostrar» también lo apaga para siempre.
 * - «Ver después», la X y Escape lo posponen: no vuelve en esta sesión del navegador, pero sí en la siguiente.
 * - El evento `openOnboarding` (menú de perfil → Guía de uso; Asistente) lo vuelve a abrir aunque ya se haya visto.
 *
 * La forma guardada en localStorage conserva los campos de la versión anterior (`currentStep` ya no se usa), para que
 * quien ya lo vio no lo vuelva a ver tras el cambio.
 */
export interface OnboardingState {
  hasSeenOnboarding: boolean;
  currentStep: number;
  dontShowAgain: boolean;
}

export const ONBOARDING_KEY = 'alertai_onboarding';
export const ONBOARDING_POSPUESTO_KEY = 'alertai_onboarding_pospuesto';
const RETRASO_MS = 500;

const initialState: OnboardingState = {
  hasSeenOnboarding: false,
  currentStep: 0,
  dontShowAgain: false,
};

const pospuesto = (): boolean => {
  try { return sessionStorage.getItem(ONBOARDING_POSPUESTO_KEY) === '1'; } catch { return false; }
};

export function useOnboarding() {
  const [state, setState] = useLocalStorage<OnboardingState>(ONBOARDING_KEY, initialState);
  const [isOpen, setIsOpen] = useState(false);

  // Primera vez: se abre solo si no se ha visto, no se apagó y no se pospuso en esta sesión.
  useEffect(() => {
    if (state.hasSeenOnboarding || state.dontShowAgain || pospuesto()) return;
    // Se vuelve a mirar `pospuesto()` al disparar: si «Guía de uso» lo abrió y se pospuso antes del medio segundo, no reabre.
    const timer = setTimeout(() => { if (!pospuesto()) setIsOpen(true); }, RETRASO_MS);
    return () => clearTimeout(timer);
  }, [state.hasSeenOnboarding, state.dontShowAgain]);

  // Reabrir desde el menú de perfil o el Asistente.
  useEffect(() => {
    const abrir = () => setIsOpen(true);
    window.addEventListener('openOnboarding', abrir);
    return () => window.removeEventListener('openOnboarding', abrir);
  }, []);

  const open = useCallback(() => setIsOpen(true), []);

  /** «Empezar»: visto para siempre. */
  const complete = useCallback(() => {
    setState((prev) => ({ ...prev, hasSeenOnboarding: true, currentStep: 0 }));
    setIsOpen(false);
  }, [setState]);

  /** «Ver después», X y Escape: se cierra y no vuelve hasta la próxima sesión del navegador. */
  const close = useCallback(() => {
    try { sessionStorage.setItem(ONBOARDING_POSPUESTO_KEY, '1'); } catch { /* sin memoria de sesión: volverá al recargar */ }
    setIsOpen(false);
  }, []);

  const setDontShowAgain = useCallback((value: boolean) => {
    setState((prev) => ({ ...prev, dontShowAgain: value }));
  }, [setState]);

  const reset = useCallback(() => {
    setState(initialState);
    try { sessionStorage.removeItem(ONBOARDING_POSPUESTO_KEY); } catch { /* nada que borrar */ }
  }, [setState]);

  return {
    isOpen,
    hasSeenOnboarding: state.hasSeenOnboarding,
    dontShowAgain: state.dontShowAgain,
    open,
    close,
    complete,
    setDontShowAgain,
    reset,
  };
}
